import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { SupportService } from '../shared/support.service';
import { formatSupportCaseNames, ManualTicket } from '../shared/support.model';
import { NotificationService } from '../../shared/services/notification.service';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  standalone: false,
  selector: 'app-manual-tickets-table',
  templateUrl: 'manual-tickets-table.component.html',
})
export class ManualTicketsTableComponent implements OnInit, OnDestroy {
  gridApi: GridApi;
  tickets: ManualTicket[] = [];
  loading = false;
  private refreshInterval: any;

  // Ticket detail modal
  selectedTicketId: number | null = null;
  showDetailModal = false;

  columnDefs: ColDef[] = [
    {
      headerName: 'Ticket ID',
      field: 'ticketId',
      width: 110,
      cellRenderer: params => {
        if (!params.value) return '';
        return `<a href="javascript:void(0)" class="ticket-link">#${params.value}</a>`;
      },
      onCellClicked: params => {
        this.openTicketDetail(params.data.ticketId);
      },
    },
    {
      headerName: 'Subject',
      valueGetter: params => params.data?.decision?.subject || '—',
      flex: 2,
      minWidth: 200,
    },
    {
      headerName: 'Phone',
      valueGetter: params => params.data?.decision?.phoneNumber || '—',
      width: 140,
    },
    {
      headerName: 'Cases',
      valueGetter: params => formatSupportCaseNames(params.data?.decision),
      width: 220,
    },
    {
      headerName: 'Action',
      valueGetter: params => params.data?.decision?.action?.type || '—',
      width: 110,
      cellRenderer: params => {
        const type = params.value;
        if (!type || type === '—') return '—';
        const badgeClass =
          {
            reply: 'badge bg-primary',
            close: 'badge bg-secondary',
            escalate: 'badge bg-warning text-dark',
            no_action: 'badge bg-light text-dark',
          }[type] || 'badge bg-info';
        return `<span class="${badgeClass}">${type}</span>`;
      },
    },
    {
      headerName: 'Confidence',
      valueGetter: params => {
        const c = params.data?.decision?.confidence;
        return typeof c === 'number' ? (c * 100).toFixed(0) + '%' : '—';
      },
      width: 100,
    },
    {
      headerName: 'Tracked At',
      field: 'trackedAt',
      width: 160,
      valueFormatter: params => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      headerName: 'Actions',
      width: 280,
      cellRenderer: params => {
        return `
          <button class="btn btn-sm btn-primary me-1 action-solve">Solve</button>
          <button class="btn btn-sm btn-outline-secondary me-1 action-close">Close</button>
          <button class="btn btn-sm btn-outline-danger action-spam">Spam</button>
        `;
      },
      onCellClicked: params => {
        const target = params.event?.target as HTMLElement;
        if (!target) return;
        const ticketId = params.data?.ticketId;
        if (!ticketId) return;

        if (target.classList.contains('action-solve')) {
          this.onSolve(ticketId);
        } else if (target.classList.contains('action-close')) {
          this.onClose(ticketId);
        } else if (target.classList.contains('action-spam')) {
          this.onSpam(ticketId);
        }
      },
    },
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: false,
    suppressMenu: true,
    cellClass: 'd-flex justify-content-start align-items-center',
  };

  constructor(
    private supportService: SupportService,
    private notificationService: NotificationService,
    private modal: ModalService
  ) {}

  ngOnInit() {
    this.loadTickets();
    this.refreshInterval = setInterval(() => this.loadTickets(), 30000);
  }

  ngOnDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
  }

  loadTickets() {
    this.loading = true;
    this.supportService.getManualTickets().subscribe({
      next: res => {
        this.tickets = res.items;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notificationService.error('Failed to load manual tickets', 'Support');
      },
    });
  }

  openTicketDetail(ticketId: number) {
    this.selectedTicketId = ticketId;
    this.showDetailModal = true;
  }

  closeDetailModal() {
    this.showDetailModal = false;
    this.selectedTicketId = null;
  }

  onSolve(ticketId: number) {
    this.supportService.solveTicket(ticketId).subscribe({
      next: result => {
        this.notificationService.success(result.message, 'Support');
        this.loadTickets();
      },
      error: () => {
        this.notificationService.error('Failed to solve ticket', 'Support');
      },
    });
  }

  onClose(ticketId: number) {
    this.supportService.closeTicket(ticketId).subscribe({
      next: result => {
        this.notificationService.success(result.message, 'Support');
        this.loadTickets();
      },
      error: () => {
        this.notificationService.error('Failed to close ticket', 'Support');
      },
    });
  }

  onSpam(ticketId: number) {
    this.modal
      .confirm()
      .open()
      .result.then(confirmed => {
        if (!confirmed) return;
        this.supportService.spamTicket(ticketId).subscribe({
          next: result => {
            this.notificationService.success(result.message, 'Support');
            this.loadTickets();
          },
          error: () => {
            this.notificationService.error('Failed to mark ticket as spam', 'Support');
          },
        });
      });
  }
}
